import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import dayjs from 'dayjs';
import {
    CONVERSATION_API,
    type loader,
} from '~/routes/remix-api.chat.conversation';
import {
    CREATE_CONVERSATION_MESSAGE_API,
    type action as createMessageAction,
} from '~/routes/remix-api.chat.create-conversation-message';
import {
    MARK_CONVERSATION_READ_API,
    type action as markConversationReadAction,
} from '~/routes/remix-api.chat.mark-conversation-read';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation, Message } from '../types';
import { createOptimisticMessage } from '../utils';
import { useConversationCache } from './use-conversation-cache';

/**
 * Hook to get the conversation between the user and another participant(s).
 */
export function useConversation({
    id,
    pollingIntervalMs = 0,
}: {
    /**
     * The conversation ID. If set to `0`, this hook will be disabled and the
     * conversation will not be fetched.
     */
    id: number;
    /**
     * The polling interval in milliseconds. Set to `0` to disable polling.
     * Defaults to `0` (disabled).
     */
    pollingIntervalMs?: number;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { getCachedConversation, cacheConversation } = useConversationCache();
    const isFetched = useRef(false);
    const [conversation, setConversation] = useState<Conversation | null>(
        getCachedConversation(id) ?? null
    );
    const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);
    const fetcher = useFetcher<typeof loader>();
    const createMessageFetcher = useFetcher<typeof createMessageAction>();
    const markAsReadFetcher = useFetcher<typeof markConversationReadAction>();
    const isHookDisabled = id <= 0 || !isLoggedIn;
    const messages = useMemo(() => {
        const serverMessages = conversation?.Messages.nodes || [];
        return [...optimisticMessages, ...serverMessages];
    }, [conversation, optimisticMessages]);
    const apiUrl = useMemo(() => {
        const url = new URL(CONVERSATION_API, window.location.origin);
        url.searchParams.set('id', String(id));
        return `${url.pathname}${url.search}`;
    }, [id]);

    const createMessage = useCallback(
        ({
            message,
            attachments,
        }: {
            message?: string;
            attachments?: File[];
        }) => {
            if (isHookDisabled || !isLoggedIn) return;
            // TODO: tracking
            const formData = new FormData();
            formData.append('conversationID', String(id));
            if (message) {
                formData.append('message', message);
            }
            if (attachments?.length) {
                attachments.forEach((attachment) => {
                    formData.append('attachments', attachment);
                });
            }
            createMessageFetcher.submit(formData, {
                action: CREATE_CONVERSATION_MESSAGE_API,
                method: 'POST',
                encType: attachments?.length
                    ? 'multipart/form-data'
                    : undefined,
            });
            setOptimisticMessages((prev) => {
                const newMessage = createOptimisticMessage({
                    message,
                    attachments,
                    conversation,
                    user,
                });
                return [newMessage, ...prev];
            });
        },
        [
            isHookDisabled,
            isLoggedIn,
            conversation,
            id,
            user,
            createMessageFetcher,
        ]
    );

    const markAsRead = useCallback(() => {
        if (isHookDisabled) return;
        markAsReadFetcher.submit(
            { conversationID: id },
            {
                action: MARK_CONVERSATION_READ_API,
                method: 'POST',
                encType: 'application/json',
            }
        );
    }, [isHookDisabled, id, markAsReadFetcher]);

    // Load the conversation details.
    useEffect(() => {
        if (isHookDisabled) {
            setConversation(null);
            setOptimisticMessages([]);
            isFetched.current = false;
        } else if (fetcher.state === 'idle' && !isFetched.current) {
            // Fetch the conversation on mount.
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (fetcher.data) {
            setConversation(fetcher.data.conversation);
            if (fetcher.state === 'idle') {
                setOptimisticMessages([]);
            }
            if (fetcher.data.conversation) {
                cacheConversation(fetcher.data.conversation);
            }
        }
    }, [isHookDisabled, fetcher, apiUrl, cacheConversation]);

    // Use the cached conversation if it exists.
    useEffect(() => {
        if (isHookDisabled) return;
        const cachedConversation = getCachedConversation(id);
        if (cachedConversation) {
            setConversation(cachedConversation);
        }
    }, [isHookDisabled, id, getCachedConversation]);

    // Polling to update the conversation details.
    useEffect(() => {
        if (isHookDisabled || pollingIntervalMs <= 0) {
            return;
        }
        const interval = setInterval(() => {
            fetcher.load(apiUrl);
        }, pollingIntervalMs);
        return () => clearInterval(interval);
    }, [isHookDisabled, pollingIntervalMs, fetcher, apiUrl]);

    // Update the optimistic messages on create message mutation success.
    useEffect(() => {
        if (
            createMessageFetcher.data &&
            createMessageFetcher.state !== 'submitting'
        ) {
            const sentMessage = createMessageFetcher.data.message;
            // When we get the result of the create message mutation, replace
            // the optimistic message with the real message.
            setOptimisticMessages((messages) => {
                const newMessages = [];
                let isReplaced = false;
                // Iterate from oldest to newest (reverse order).
                for (const message of [...messages].reverse()) {
                    if (isReplaced) {
                        newMessages.unshift(message);
                        continue;
                    }
                    // Find the corresponding optimistic message by verifying
                    // the message type, content, and similar timestamp.
                    const optimisticTimestamp = dayjs
                        .utc(message.Created)
                        .unix();
                    const sentTimestamp = dayjs.utc(sentMessage.Created).unix();
                    if (
                        message.isOptimistic &&
                        message.__typename === sentMessage.__typename &&
                        // Check content is the same.
                        message.Content === sentMessage.Content &&
                        // Check number of attachments are the same.
                        (message.__typename !== 'FileAttachmentMessage' ||
                            sentMessage.__typename !==
                                'FileAttachmentMessage' ||
                            message.Attachments.length ===
                                sentMessage.Attachments.length) &&
                        // Check the timestamp is within 10 seconds.
                        Math.abs(optimisticTimestamp - sentTimestamp) < 10
                    ) {
                        newMessages.unshift({
                            ...sentMessage,
                            isOptimistic: false,
                        });
                        isReplaced = true;
                    } else {
                        newMessages.unshift(message);
                    }
                }
                return newMessages;
            });
        }
    }, [createMessageFetcher]);

    // Update the conversation status on mark as read mutation success.
    useEffect(() => {
        if (
            markAsReadFetcher.data &&
            markAsReadFetcher.state !== 'submitting'
        ) {
            const updatedConversation = markAsReadFetcher.data.conversation;
            setConversation((prev) => {
                if (!prev) {
                    return prev;
                }
                const newConversation = {
                    ...prev,
                    Unread: updatedConversation.Unread,
                    UnreadMessageCount: updatedConversation.UnreadMessageCount,
                };
                cacheConversation(newConversation);
                return newConversation;
            });
        }
    }, [markAsReadFetcher, cacheConversation]);

    return {
        /**
         * The conversation details. This is `null` if not fetched yet.
         */
        conversation,
        /**
         * The messages in the conversation ordered by earliest first. This includes
         * sent messages that are not in the conversation yet (optimistic UI).
         * Prefer to use this instead of the messages from the conversation object.
         */
        messages,
        /**
         * The create message fetcher is submitting.
         */
        isSubmitting: createMessageFetcher.state === 'submitting',
        /**
         * The mark as read fetcher is not idle.
         */
        isMarkAsReadBusy: markAsReadFetcher.state !== 'idle',
        /**
         * Refetch the conversation details.
         */
        refetch: useCallback(() => {
            if (isHookDisabled) return;
            fetcher.load(apiUrl);
        }, [isHookDisabled, fetcher, apiUrl]),
        /**
         * Create a new message in the conversation.
         */
        createMessage,
        /**
         * Mark the conversation as read.
         */
        markAsRead,
    };
}
